import React, { useState, useCallback, useMemo } from 'react';
import { withMainLayout } from 'hocs';
import { useTranslation } from 'react-i18next';
import { downloadDmsDocument } from 'utils/downloadDmsDocument';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';
import Card from 'components/Card/Card';
import { getSetupData } from 'additiv-services/setup';
import { useNavigate, useLocation } from 'react-router-dom';
import ServiceManager from 'services/ServiceManager';
import {
    Modal, notification, Tabs, TabPane,
} from '../../ui-library';
import { START_PAGINATION_PAGE } from './constants/const';
import { adaptDocLibrary } from './utils/adaptDocLibrary';
import { useOptionsDocLibrary } from './hooks/useOptionsDocLibrary';
import { useManagerProfile } from '../../prodivers/managerProfile';
import { useGeneralDocuments, GENERAL_DOCUMENTS_QUERY_API } from './hooks/useGeneralDocuments';
import { useDocLibraryProducts } from './hooks/useDocLibraryProducts';

import UploadDocumentModal from './components/UploadDocumentModal';
import GeneralDocuments from './components/GeneralDocuments';
import BundledDocuments from './components/BundledDocuments';

import UploadBundledDocuments from './components/BundledDocuments/UploadBundledDocuments';
import './DocLibraryPage.css';

type DocumentData = {
    documentMetadataName: string;
    documentCategoryId: string;
    productId: string;
    [key: string]: unknown;
};

const setupData = getSetupData();

const DocLibraryPage = () => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const { search } = useLocation();

    const [searchTerm, setSearchTerm] = useState('');
    const [downloadInProgress, setDownloadInProgress] = useState<Record<string, boolean>>({});
    const [deleteInProgress, setDeleteInProgress] = useState<Record<string, boolean>>({});
    const [productId, setProductId] = useState<string | null>(null);
    const [currentPagination, setCurrentPagination] = useState(START_PAGINATION_PAGE);
    const [showModal, setShowModal] = useState(false);

    const searchParams = useMemo(() => new URLSearchParams(search), [search]);
    const openedUploadBundledDocuments = searchParams.get('uploadType') === 'bundle';

    const { hasAccessPolicy } = useManagerProfile();

    const { documentCategoriesList, isLoading: isDocOptionsLoading } = useOptionsDocLibrary();

    const documentCategoryId = documentCategoriesList && documentCategoriesList.length
        ? documentCategoriesList[0].id
        : null;

    const {
        data: documentsResponse,
        isLoading,
        error: docError,
        refetch: refetchDocuments,
    } = useGeneralDocuments();

    const fullData = (documentsResponse as { data?: DocumentData[] } | null)?.data ?? [];

    const { data: productList = [] } = useDocLibraryProducts();

    const productOptions = useMemo(() => {
        const defaultItem = { id: '', label: t('clientDashboard.documents.all') };

        return [defaultItem, ...productList];
    }, [productList, t]);

    const { mutate } = useGenericAPIMutation();

    const deleteDocument = useCallback(
        (documentId: string) => {
            setDeleteInProgress((prev) => ({ ...prev, [documentId]: true }));
            mutate(
                {
                    service: 'customDocumentsService',
                    API: 'deleteDocumentById',
                    payload: { documentId },
                    invalidateQueryKeys: [`getData_${GENERAL_DOCUMENTS_QUERY_API}`],
                },
                {
                    onSuccess: () => {
                        setDeleteInProgress((prev) => ({ ...prev, [documentId]: false }));
                        notification.open({
                            content: t('advisoryDashboard.documents.documentDeletedSuccess'),
                            type: 'success',
                        });
                    },
                    onError: () => {
                        setDeleteInProgress((prev) => ({ ...prev, [documentId]: false }));
                    },
                },
            );
        },
        [mutate, t],
    );

    const deleteDocumentConfirmation = useCallback(
        (documentId: string, content: string) => {
            Modal.confirm({
                title: t('advisoryDashboard.documents.deleteTitle'),
                content,
                okText: t('clientDashboard.yes'),
                onOk: async () => deleteDocument(documentId),
                cancelText: t('clientDashboard.no'),
                className: 'close-client-modal',
            });
        },
        [t, deleteDocument],
    );

    const downloadDocument = useCallback(
        async (documentId: string, fileName: string) => {
            try {
                setDownloadInProgress((prev) => ({
                    ...prev,
                    [documentId]: true,
                }));
                const { data } = await ServiceManager.documents('getDocumentById', [
                    documentId,
                ]) as any;

                const { newBlob } = downloadDmsDocument(data, {
                    name: fileName,
                    saveFile: true,
                });
                const url = URL.createObjectURL(newBlob);

                globalThis.open(url, '_blank');
            } catch (err) {
                // no-op to preserve current behavior
            } finally {
                setDownloadInProgress((prev) => ({
                    ...prev,
                    [documentId]: false,
                }));
            }
        },
        [],
    );

    const onChangePage = useCallback((paging: { currentPage: number }) => {
        setCurrentPagination(paging?.currentPage);
    }, []);

    const filteredData = useMemo(
        () => fullData
            .filter(
                (f) => !searchTerm
                        || f.documentMetadataName
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()),
            )
            .filter(
                (f) => !documentCategoryId
                        || parseInt(f.documentCategoryId, 10)
                            === parseInt(documentCategoryId, 10),
            )
            .filter(
                (f) => !productId
                        || parseInt(f.productId, 10) === parseInt(productId, 10),
            ),
        [searchTerm, documentCategoryId, productId, fullData],
    );

    const tableData = useMemo(
        () => adaptDocLibrary(
                filteredData as never[],
                downloadDocument,
                downloadInProgress,
                deleteDocumentConfirmation,
                deleteInProgress,
        ),
        [
            filteredData,
            downloadDocument,
            downloadInProgress,
            deleteDocumentConfirmation,
            deleteInProgress,
        ],
    );

    const currentTab = searchParams.get('tab') || 'generalDocuments';
    const documentBundleId = searchParams.get('bundleId');

    // Leaving the upload screen always lands on the bundle documents tab with the filters reset.
    const backToBundledDocuments = useCallback(
        () => navigate(
            documentBundleId
                ? `/documents?tab=bundledDocuments&bundleId=${documentBundleId}`
                : '/documents?tab=bundledDocuments',
        ),
        [navigate, documentBundleId],
    );

    const bundleProductOptions = useMemo(
        () => productList.map(({ id, label }) => ({ value: id, label })),
        [productList],
    );

    const handleCurrentTab = (key: string) => {
        navigate(`/documents?tab=${key}`);
    };

    const documentTabs = useMemo(
        () => [
            {
                label: t('advisoryDashboard.documents.GeneralDocuments'),
                value: 'generalDocuments',
            },
            {
                label: t('advisoryDashboard.documents.BundledDocuments'),
                value: 'bundledDocuments',
            },
        ],
        [t],
    );

    return (
        <div className="container mb-40">
            {!openedUploadBundledDocuments && (
                <Card title={t('advisoryDashboard.documents.title')}>
                    {setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG && hasAccessPolicy('ManageBundleDocuments') && (
                        <Tabs
                            activeKey={currentTab}
                            onChange={(key) => handleCurrentTab(key)}
                            className="doc-library__documents-tabs"
                        >
                            {documentTabs.map((docType) => (
                                <TabPane
                                    tab={(
                                        <div className="tab-title">
                                            <span>{docType.label}</span>
                                        </div>
                                    )}
                                    key={docType.value}
                                />
                            ))}
                        </Tabs>
                    )}
                    {currentTab === 'generalDocuments' && (
                        <GeneralDocuments
                            t={t}
                            productOptions={productOptions}
                            productId={productId}
                            setProductId={setProductId}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            filteredData={filteredData}
                            isLoading={isLoading}
                            docError={docError}
                            currentPagination={currentPagination}
                            tableData={tableData}
                            onChangePage={onChangePage}
                            hasAccessPolicy={hasAccessPolicy}
                            setShowModal={setShowModal}
                        />
                    )}
                    {currentTab === 'bundledDocuments' && (
                        <BundledDocuments
                            productList={productList}
                            hasAccessPolicy={hasAccessPolicy}
                            handleOpenUploadPage={() => {
                                navigate(
                                    `/documents?uploadType=bundle${documentBundleId ? `&bundleId=${documentBundleId}` : ''}`,
                                );
                            }}
                        />
                    )}
                </Card>
            )}

            {openedUploadBundledDocuments && (
                <Card
                    title={t(
                        'advisoryDashboard.documents.uploadDocumentTitle',
                    )}
                >
                    <UploadBundledDocuments
                        onBack={backToBundledDocuments}
                        onSuccess={backToBundledDocuments}
                        backDisabled={false}
                        saveButtonDisabled={false}
                        productOptions={bundleProductOptions}
                    />
                </Card>
            )}
            {showModal && (
                <UploadDocumentModal
                    isLoading={isDocOptionsLoading}
                    onClose={() => setShowModal(false)}
                    documentCategories={documentCategoriesList}
                    documentCategoryId={documentCategoryId}
                    productsList={productList}
                    getDocumentLists={refetchDocuments}
                />
            )}
        </div>
    );
};

export default withMainLayout(DocLibraryPage);
